import React from "react";

// Presentational venue seat map: stage marker + colour-coded sections + row labels + aisle gaps.
// sections: [{ tier_id, name, color, price?, aisle_after, table_mode, seats:[{label,taken,row,col}] }]
// variant "book" (default) = buyer selection; "heatmap" = organiser sold view (taken=sold, warm colour).
export const SeatMap = ({ sections = [], stageLabel = "STAGE", stagePosition = "top", selected = {}, onSeat, onSelectTable, interactive = true, variant = "book" }) => {
  const heat = variant === "heatmap";
  const [focusTier, setFocusTier] = React.useState("");
  const sectionRefs = React.useRef({});
  const toggleFocus = (tid) => {
    const next = focusTier === tid ? "" : tid;
    setFocusTier(next);
    if (next && sectionRefs.current[next]) sectionRefs.current[next].scrollIntoView({ behavior: "smooth", block: "nearest" });
  };
  const Stage = () => (
    <div className="flex justify-center my-3" data-testid="seatmap-stage">
      <div className="w-2/3 max-w-md text-center text-[11px] font-semibold tracking-[0.3em] text-[#5a6b80] bg-gradient-to-b from-[#e8edf3] to-[#f6f8fb] border border-gray-200 rounded-md py-1.5 shadow-inner">
        {stageLabel || "STAGE"}
      </div>
    </div>
  );

  const SeatBtn = ({ s, seat }) => {
    const sel = (selected[s.tier_id] || []).includes(seat.label);
    if (heat) {
      return (
        <span data-testid={`seat-${s.tier_id}-${seat.label}`} title={`${formatSeat(seat.label)} — ${seat.taken ? "sold" : "available"}`}
          className="w-7 h-7 rounded-md text-[9px] font-medium flex items-center justify-center shrink-0"
          style={{ backgroundColor: seat.taken ? "#ef4444" : "#e6f6f0", color: seat.taken ? "#fff" : "#5a6b80" }}>
          {seat.col + 1}
        </span>
      );
    }
    return (
      <button type="button" disabled={!interactive || seat.taken}
        onClick={() => interactive && onSeat && onSeat(s.tier_id, seat.label, seat.taken)}
        data-testid={`seat-${s.tier_id}-${seat.label}`} title={`${s.name} · ${formatSeat(seat.label)}`}
        className={`w-7 h-7 rounded-md text-[9px] font-medium transition shrink-0 ${
          seat.taken ? "bg-gray-400 text-white/70 line-through cursor-not-allowed"
          : sel ? "text-white shadow-sm scale-105"
          : "text-[#16243b] hover:brightness-95"}`}
        style={!seat.taken ? { backgroundColor: sel ? s.color : `${s.color}33` } : undefined}>
        {seat.col + 1}
      </button>
    );
  };

  const Section = ({ s }) => {
    const rowsMap = {};
    (s.seats || []).forEach((seat) => {
      (rowsMap[seat.row] = rowsMap[seat.row] || []).push(seat);
    });
    const rowIdxs = Object.keys(rowsMap).map(Number).sort((a, b) => a - b);
    const aisles = new Set((s.aisle_after || []).map(Number));
    const sold = (s.seats || []).filter((x) => x.taken).length;
    const header = (
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="w-3 h-3 rounded-sm" style={{ backgroundColor: s.color }} />
        <span className="text-sm font-semibold text-[#16243b]">{s.name}</span>
        {s.price != null && <span className="text-xs text-[#8a97a8]">£{Number(s.price).toFixed(2)}</span>}
        {heat && <span className="text-xs font-medium text-[#ef4444]" data-testid={`heat-pct-${s.tier_id}`}>{sold}/{(s.seats || []).length} sold ({Math.round((sold / ((s.seats || []).length || 1)) * 100)}%)</span>}
      </div>
    );

    // Round-table layout: each "row" is a table rendered as a labelled card.
    if (s.table_mode) {
      return (
        <div className={`mb-4 transition-opacity ${focusTier && focusTier !== s.tier_id ? "opacity-30" : ""}`} ref={(el) => (sectionRefs.current[s.tier_id] = el)} data-testid={`seatmap-section-${s.tier_id}`}>
          {header}
          <div className="flex flex-wrap gap-3 justify-center">
            {rowIdxs.map((r) => {
              const tblSeats = rowsMap[r].sort((a, b) => a.col - b.col);
              const free = tblSeats.filter((x) => !x.taken).map((x) => x.label);
              const isFullTable = s.seats_per_table > 0 && free.length >= s.seats_per_table;
              return (
                <div key={r} data-testid={`table-${s.tier_id}-${r + 1}`}
                  className="rounded-2xl border-2 px-3 py-2.5 flex flex-col items-center" style={{ borderColor: `${s.color}55` }}>
                  <span className="text-[10px] font-semibold text-[#5a6b80] mb-1.5">Table {r + 1}</span>
                  <div className="grid grid-cols-5 gap-1">
                    {tblSeats.map((seat) => <SeatBtn key={seat.label} s={s} seat={seat} />)}
                  </div>
                  {!heat && interactive && onSelectTable && free.length > 0 && (
                    <button type="button" onClick={() => onSelectTable(s.tier_id, free)} data-testid={`book-table-${s.tier_id}-${r + 1}`}
                      className="mt-2 text-[10px] font-medium rounded-full px-2.5 py-1 text-white" style={{ backgroundColor: s.color }}>
                      {isFullTable ? `Book whole table (${free.length})${s.table_discount_pct > 0 ? ` · save ${s.table_discount_pct}%` : ""}` : `Book remaining (${free.length})`}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      );
    }

    return (
      <div className={`mb-4 transition-opacity ${focusTier && focusTier !== s.tier_id ? "opacity-30" : ""}`} ref={(el) => (sectionRefs.current[s.tier_id] = el)} data-testid={`seatmap-section-${s.tier_id}`}>
        {header}
        <div className="overflow-x-auto pb-1">
          <div className="w-max mx-auto space-y-1.5">
          {rowIdxs.map((r) => {
            const seats = rowsMap[r].sort((a, b) => a.col - b.col);
            const rowLabel = (seats[0]?.label || "").replace(/\d+$/, "");
            return (
              <div key={r} className="flex items-center gap-1.5">
                <span className="w-4 text-[10px] text-[#8a97a8] text-right shrink-0">{rowLabel}</span>
                {seats.map((seat) => {
                  const gap = aisles.has(seat.col + 1);
                  return (
                    <React.Fragment key={seat.label}>
                      <SeatBtn s={s} seat={seat} />
                      {gap && <span className="w-4 shrink-0" />}
                    </React.Fragment>
                  );
                })}
              </div>
            );
          })}
          </div>
        </div>
      </div>
    );
  };

  const hasWideGrid = sections.some((s) => !s.table_mode && (s.seats || []).some((x) => x.col >= 9));
  const anyTaken = sections.some((s) => (s.seats || []).some((x) => x.taken));
  const priced = sections.filter((s) => s.price != null);

  return (
    <div className="rounded-xl bg-[#fbfcfe] border border-gray-100 p-3" data-testid="seatmap">
      {stagePosition === "top" && <Stage />}
      {sections.map((s) => <Section key={s.tier_id} s={s} />)}
      {stagePosition === "bottom" && <Stage />}
      {/* Legend */}
      {priced.length > 0 && (
        <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 mt-2 pt-2 border-t border-gray-100" data-testid="seatmap-legend">
          {priced.map((s) => (
            <button type="button" key={s.tier_id} onClick={() => toggleFocus(s.tier_id)} data-testid={`legend-${s.tier_id}`}
              className={`inline-flex items-center gap-1.5 text-[11px] rounded-full px-2 py-0.5 transition ${focusTier === s.tier_id ? "bg-[#16243b] text-white" : "text-[#5a6b80] hover:bg-gray-100"}`}>
              <span className="w-3 h-3 rounded" style={{ backgroundColor: s.color }} />
              {s.name} · £{Number(s.price).toFixed(2)}
            </button>
          ))}
          {focusTier && <button type="button" onClick={() => toggleFocus(focusTier)} data-testid="legend-clear" className="text-[11px] text-[#1fb0b0] hover:underline">show all</button>}
          {heat && <span className="inline-flex items-center gap-1.5 text-[11px] text-[#5a6b80]"><span className="w-3 h-3 rounded bg-[#ef4444]" /> sold</span>}
          {heat && <span className="inline-flex items-center gap-1.5 text-[11px] text-[#5a6b80]"><span className="w-3 h-3 rounded bg-[#e6f6f0]" /> available</span>}
          {!heat && anyTaken && <span className="inline-flex items-center gap-1.5 text-[11px] text-[#5a6b80]"><span className="w-3 h-3 rounded bg-gray-400" /> taken</span>}
        </div>
      )}
      {!heat && interactive && (
        <p className="text-[11px] text-[#8a97a8] text-center mt-1">
          Tap a seat to select{hasWideGrid ? " · scroll sideways to see every seat →" : ""}
        </p>
      )}
    </div>
  );
};

// Friendly seat label for UI: T3S5 -> "Table 3 · Seat 5"
export const formatSeat = (label) => {
  if (!label) return "";
  const m = /^T(\d+)S(\d+)$/.exec(label);
  return m ? `Table ${m[1]} · Seat ${m[2]}` : label;
};

// Build sections from backend product.seat_availability (+ tier price/name)
export const availToSections = (product) => {
  const avail = product.seat_availability || {};
  return (product.ticket_tiers || [])
    .filter((t) => avail[t.id])
    .map((t) => ({ tier_id: t.id, name: t.name, price: t.price, color: avail[t.id].color, aisle_after: avail[t.id].aisle_after || [],
      table_mode: avail[t.id].table_mode, tables: avail[t.id].tables, seats_per_table: avail[t.id].seats_per_table,
      table_discount_pct: avail[t.id].table_discount_pct || 0,
      seats: avail[t.id].seats || [] }));
};

// Build a preview layout client-side from unsaved designer tier config (no taken info)
const seatLabel = (r, c) => {
  let label = "", n = r;
  while (true) { label = String.fromCharCode(65 + (n % 26)) + label; n = Math.floor(n / 26) - 1; if (n < 0) break; }
  return `${label}${c + 1}`;
};
const PALETTE = ["#2ec9c9", "#f59e0b", "#8b5cf6", "#ef4444", "#10b981", "#3b82f6", "#ec4899", "#14b8a6"];
export const tiersToSections = (tiers = []) => {
  return tiers.map((t, i) => {
    const seats = [];
    if (t.table_mode) {
      const tables = parseInt(t.tables) || 0;
      const spt = parseInt(t.seats_per_table) || 0;
      for (let tb = 0; tb < tables; tb++) for (let s = 0; s < spt; s++) seats.push({ label: `T${tb + 1}S${s + 1}`, taken: false, row: tb, col: s });
      return { tier_id: t.id || `new-${i}`, name: t.name || `Section ${i + 1}`, price: parseFloat(t.price) || 0,
        color: t.section_color || PALETTE[i % PALETTE.length], table_mode: true, tables, seats_per_table: spt, table_discount_pct: parseFloat(t.table_discount_pct) || 0, aisle_after: [], seats };
    }
    const rowSeats = (t.row_seats && t.row_seats.length) ? t.row_seats : null;
    const rows = parseInt(t.seat_rows) || 0;
    const cols = parseInt(t.seat_cols) || 0;
    const grid = rowSeats ? rowSeats.map((n) => parseInt(n) || 0) : Array.from({ length: rows }, () => cols);
    grid.forEach((n, r) => { for (let c = 0; c < n; c++) seats.push({ label: seatLabel(r, c), taken: false, row: r, col: c }); });
    return { tier_id: t.id || `new-${i}`, name: t.name || `Section ${i + 1}`, price: parseFloat(t.price) || 0,
      color: t.section_color || PALETTE[i % PALETTE.length], aisle_after: (t.aisle_after || []), seats };
  }).filter((s) => s.seats.length);
};
